import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, NotebookPen, Pencil, Plus, Printer, Trash2, Undo2, Users } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { PageLoader } from "@/components/ui/Spinner";
import { useDocumentationCluster, useDeleteCluster, useUnsettleClusters } from "../api/clusters";
import type { DocumentationCluster, DesignDocument } from "../types";
import {
  useClusterDocuments,
  useEditDocumentNotes,
  useApproveDocument,
  useRejectDocument,
  useWithdrawDocument,
} from "../api/documents";
import { AddDocumentDialog } from "../components/AddDocumentDialog";
import { downloadFileTracked, FileDownloadStatusBadge } from "../components/FileDownloadStatus";
import { ClusterMembersDialog } from "../components/ClusterMembersDialog";
import { EditClusterDialog } from "../components/EditClusterDialog";
import { RevisionHistory } from "../components/RevisionHistory";
import { CLUSTER_TYPE_LABELS, STATUS_LABELS } from "../types";
import { usePageTitle } from "@/hooks/usePageTitle";

function sortDocuments(docs: DesignDocument[], userId: string | null, isManager: boolean): DesignDocument[] {
  if (!isManager) {
    // Contractor / non-manager: pending first (oldest created_at = longest wait), then others newest first
    return [...docs].sort((a, b) => {
      const ap = a.current_status === "pending_signature";
      const bp = b.current_status === "pending_signature";
      if (ap && !bp) return -1;
      if (!ap && bp) return 1;
      if (ap && bp) return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }
  // Manager: pending-approval first, then my unacknowledged signed, then pending signature,
  // then closed documents (rejected / withdrawn) last
  return [...docs].sort((a, b) => {
    const apa = a.approval_status === "pending_approval";
    const apb = b.approval_status === "pending_approval";
    const ara = a.approval_status === "rejected" || a.approval_status === "withdrawn";
    const arb = b.approval_status === "rejected" || b.approval_status === "withdrawn";
    if (ara && !arb) return 1;
    if (!ara && arb) return -1;
    if (apa && !apb) return -1;
    if (!apa && apb) return 1;
    const myUnacked = (doc: DesignDocument) =>
      doc.revisions.some(
        (r) => r.status === "signed" && !r.acknowledged_at && userId && String(r.created_by) === userId
      );
    const au = myUnacked(a);
    const bu = myUnacked(b);
    if (au && !bu) return -1;
    if (!au && bu) return 1;
    const ap = a.current_status === "pending_signature";
    const bp = b.current_status === "pending_signature";
    if (ap && !bp) return -1;
    if (!ap && bp) return 1;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}

const STATUS_BADGE_COLOR: Record<string, "yellow" | "green" | "red" | "gray"> = {
  pending_signature: "yellow",
  signed: "green",
  rejected: "red",
};

function DocumentNotes({
  clusterId,
  document,
}: {
  clusterId: string;
  document: DesignDocument;
}) {
  // Backend already enforces visibility — if the field is null we are not the author.
  if (!document.can_edit_notes) return null;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(document.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const mutation = useEditDocumentNotes(clusterId, document.id);

  const value = document.notes ?? "";

  async function save() {
    setError(null);
    if (draft.length > 4000) {
      setError("Notatka może mieć maks. 4000 znaków.");
      return;
    }
    try {
      await mutation.mutateAsync(draft);
      setEditing(false);
    } catch {
      setError("Nie udało się zapisać notatki.");
    }
  }

  return (
    <div className="mt-3 rounded-md border border-blue-200 bg-blue-50/60 dark:border-blue-800/40 dark:bg-blue-900/15 overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-blue-200/70 dark:border-blue-800/40 bg-blue-100/60 dark:bg-blue-900/25 px-3 py-1.5">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
          <NotebookPen size={12} className="flex-shrink-0" />
          Notatki <span className="text-[10px] font-normal lowercase tracking-normal text-blue-500 dark:text-blue-400">(tylko ja)</span>
        </span>
        {!editing && (
          <button
            type="button"
            onClick={() => { setDraft(value); setEditing(true); }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {value ? "Edytuj" : "Dodaj"}
          </button>
        )}
      </div>
      <div className="px-3 py-2.5">
        {!editing ? (
          value ? (
            <p className="text-xs text-blue-900 dark:text-blue-100 whitespace-pre-wrap">{value}</p>
          ) : (
            <p className="text-xs italic text-blue-500/70 dark:text-blue-400/70">
              Brak notatki — kliknij „Dodaj", aby zostawić sobie przypomnienie.
            </p>
          )
        ) : (
          <div className="space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              maxLength={4000}
              autoFocus
              className="w-full border border-blue-300 dark:border-blue-700 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="np. Odesłać potem projektantowi branży elektroenergetycznej"
            />
            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
            <div className="flex gap-2">
              <Button type="button" onClick={save} disabled={mutation.isPending} className="text-xs py-1 px-3">
                {mutation.isPending ? "Zapisywanie…" : "Zapisz"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setDraft(value); setEditing(false); setError(null); }}
                className="text-xs py-1 px-3"
              >
                Anuluj
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ApproveDocumentButton({
  clusterId,
  document,
  clusterType,
  onApproved,
}: {
  clusterId: string;
  document: DesignDocument;
  clusterType: DocumentationCluster["cluster_type"];
  onApproved: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const approveMutation = useApproveDocument(clusterId, document.id);
  const rejectMutation = useRejectDocument(clusterId, document.id);

  async function handleApprove() {
    setError(null);
    try {
      await approveMutation.mutateAsync();
      onApproved();
    } catch {
      setError("Nie udało się zaakceptować dokumentu.");
    }
  }

  async function handleReject() {
    setError(null);
    if (rejectReason.length > 2000) {
      setError("Powód odrzucenia może mieć maks. 2000 znaków.");
      return;
    }
    try {
      await rejectMutation.mutateAsync({ reason: rejectReason.trim() || undefined });
      onApproved();
    } catch {
      setError("Nie udało się odrzucić dokumentu.");
    }
  }

  const originalFile = document.revisions[0]?.original_file ?? null;
  const isDirectorAcceptance = clusterType === "director_acceptance";
  const isBusy = approveMutation.isPending || rejectMutation.isPending;

  return (
    <div className={`mt-3 rounded-md border px-3 py-2.5 ${
      isDirectorAcceptance
        ? "border-purple-200 bg-purple-50 dark:border-purple-800/40 dark:bg-purple-900/15"
        : "border-green-200 bg-green-50 dark:border-green-800/40 dark:bg-green-900/15"
    }`}>
      <p className={`text-xs mb-2 ${
        isDirectorAcceptance
          ? "text-purple-800 dark:text-purple-300"
          : "text-green-800 dark:text-green-300"
      }`}>
        {isDirectorAcceptance
          ? "Dokument oczekuje na akceptację dyrektora."
          : "Dokument dodany przez pracownika — wymaga akceptacji przed przekazaniem do projektanta."}
      </p>
      {originalFile?.url && (
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <button
            type="button"
            onClick={() =>
              downloadFileTracked(originalFile.id, originalFile.url!, originalFile.original_filename)
            }
            className={`inline-flex items-center gap-1.5 text-xs hover:underline ${
              isDirectorAcceptance
                ? "text-purple-700 dark:text-purple-400"
                : "text-green-700 dark:text-green-400"
            }`}
          >
            <Download size={12} />
            {originalFile.original_filename}
          </button>
          <FileDownloadStatusBadge fileId={originalFile.id} />
        </div>
      )}
      {error && <p className="text-xs text-red-600 dark:text-red-400 mb-1">{error}</p>}
      {!rejecting ? (
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={handleApprove}
            disabled={isBusy}
            className={`text-xs py-1 px-3 text-white ${
              isDirectorAcceptance
                ? "bg-purple-600 hover:bg-purple-700"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {approveMutation.isPending ? "Akceptowanie…" : "Akceptuj dokument"}
          </Button>
          <Button
            type="button"
            onClick={() => { setRejecting(true); setError(null); }}
            disabled={isBusy}
            className="text-xs py-1 px-3 text-white bg-red-600 hover:bg-red-700"
          >
            Odrzuć dokument
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Powód odrzucenia <span className="text-gray-400">(opcjonalnie)</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              maxLength={2000}
              autoFocus
              className="w-full border border-red-300 dark:border-red-800/60 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="np. Brak podpisu na ostatniej stronie, proszę uzupełnić i dodać ponownie."
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleReject}
              disabled={isBusy}
              className="text-xs py-1 px-3 text-white bg-red-600 hover:bg-red-700"
            >
              {rejectMutation.isPending ? "Odrzucanie…" : "Odrzuć dokument"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setRejecting(false); setRejectReason(""); setError(null); }}
              disabled={isBusy}
              className="text-xs py-1 px-3"
            >
              Anuluj
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function WithdrawDocumentButton({
  clusterId,
  document,
  onWithdrawn,
}: {
  clusterId: string;
  document: DesignDocument;
  onWithdrawn: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const withdrawMutation = useWithdrawDocument(clusterId, document.id);

  async function handleWithdraw() {
    setError(null);
    if (reason.length > 2000) {
      setError("Powód wycofania może mieć maks. 2000 znaków.");
      return;
    }
    try {
      await withdrawMutation.mutateAsync({ reason: reason.trim() || undefined });
      onWithdrawn();
    } catch {
      setError("Nie udało się wycofać dokumentu.");
    }
  }

  return (
    <div className="mt-2">
      {error && <p className="text-xs text-red-600 dark:text-red-400 mb-1">{error}</p>}
      {!confirming ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => { setConfirming(true); setError(null); }}
          className="text-xs py-1 px-3 border border-orange-300 dark:border-orange-800/60 text-orange-700 dark:text-orange-300"
        >
          <Undo2 size={12} className="inline mr-1" />
          Wycofaj z akceptacji
        </Button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-orange-800 dark:text-orange-300">
            Dokument wróci do Ciebie i zniknie z listy do sprawdzenia. Aby wysłać go ponownie,
            dodaj dokument jeszcze raz.
          </p>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Powód wycofania <span className="text-gray-400">(opcjonalnie)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={2000}
              autoFocus
              className="w-full border border-orange-300 dark:border-orange-800/60 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="np. Wysłane przez pomyłkę — przygotowuję poprawioną wersję."
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleWithdraw}
              disabled={withdrawMutation.isPending}
              className="text-xs py-1 px-3 text-white bg-orange-600 hover:bg-orange-700"
            >
              {withdrawMutation.isPending ? "Wycofywanie…" : "Tak, wycofaj dokument"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setConfirming(false); setReason(""); setError(null); }}
              disabled={withdrawMutation.isPending}
              className="text-xs py-1 px-3"
            >
              Anuluj
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentRow({
  document,
  cluster,
  isManager,
  canApproveDoc,
  onRefetch,
}: {
  document: DesignDocument;
  cluster: DocumentationCluster;
  isManager: boolean;
  canApproveDoc: boolean;
  onRefetch: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isPendingApproval = document.approval_status === "pending_approval";
  const isRejected = document.approval_status === "rejected";
  const isWithdrawn = document.approval_status === "withdrawn";
  const isDirectorAcceptance = cluster.cluster_type === "director_acceptance";
  // Whoever may check the document rules on it with accept/reject — withdrawing is
  // for the other side: the author who sent it (and managers cleaning up after them).
  const canWithdrawDoc = document.can_withdraw && !canApproveDoc;

  return (
    <div className={`border rounded-lg overflow-hidden ${
      isPendingApproval
        ? "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800/40"
        : isRejected
          ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800/40"
          : isWithdrawn
            ? "bg-gray-50 dark:bg-gray-900/40 border-gray-300 dark:border-gray-700"
            : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
    }`}>
      <button
        type="button"
        className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 sm:px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="font-medium text-gray-900 dark:text-gray-100 text-sm break-words">
            {document.title}
          </span>
          {isPendingApproval ? (
            <Badge color="orange">Oczekuje na akceptację</Badge>
          ) : isRejected ? (
            <Badge color="red">Odrzucony przez GK</Badge>
          ) : isWithdrawn ? (
            <Badge color="gray">Wycofany z akceptacji</Badge>
          ) : document.current_status ? (
            <Badge color={STATUS_BADGE_COLOR[document.current_status] ?? "gray"}>
              {STATUS_LABELS[document.current_status]}
            </Badge>
          ) : (
            <Badge color="gray">Brak rewizji</Badge>
          )}
          {cluster.signing_mode === "paper" && document.quantity > 1 && (
            <Badge color="orange">×{document.quantity}</Badge>
          )}
          {document.can_edit_notes && document.notes && (
            <Badge color="blue">
              <NotebookPen size={10} className="inline mr-0.5" /> Notatka
            </Badge>
          )}
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0 sm:ml-4">
          {document.revisions.length} {document.revisions.length === 1 ? "wersja" : "wersje"}
          {" · "}
          {expanded ? "zwiń ▲" : "rozwiń ▼"}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 px-4 pb-4 bg-gray-50 dark:bg-gray-850">
          <DocumentNotes clusterId={cluster.id} document={document} />
          {isPendingApproval && canApproveDoc && (
            <ApproveDocumentButton
              clusterId={cluster.id}
              document={document}
              clusterType={cluster.cluster_type}
              onApproved={onRefetch}
            />
          )}
          {isPendingApproval && (document.can_edit_notes || canWithdrawDoc) && (() => {
            const file = document.revisions[0]?.original_file ?? null;
            if (!file?.url && !canWithdrawDoc) return null;
            return (
              <div className="mt-3 rounded-md border border-orange-200 bg-orange-50/60 dark:border-orange-800/40 dark:bg-orange-900/15 px-3 py-2.5">
                <p className="text-xs text-orange-700 dark:text-orange-300 mb-1.5">
                  {isDirectorAcceptance
                    ? "Dokument oczekuje na akceptację dyrektora."
                    : document.can_edit_notes
                      ? "Twój dokument oczekuje na akceptację GK."
                      : "Dokument oczekuje na akceptację GK."}
                </p>
                {file?.url && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => downloadFileTracked(file.id, file.url!, file.original_filename)}
                      className="inline-flex items-center gap-1.5 text-xs text-orange-700 dark:text-orange-400 hover:underline"
                    >
                      <Download size={12} />
                      {file.original_filename}
                    </button>
                    <FileDownloadStatusBadge fileId={file.id} />
                  </div>
                )}
                {canWithdrawDoc && (
                  <WithdrawDocumentButton
                    clusterId={cluster.id}
                    document={document}
                    onWithdrawn={onRefetch}
                  />
                )}
              </div>
            );
          })()}
          {isRejected && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50/60 dark:border-red-800/40 dark:bg-red-900/15 px-3 py-2.5">
              <p className="text-xs text-red-800 dark:text-red-300">
                Dokument odrzucony przez GK
                {document.rejected_by_name ? ` — ${document.rejected_by_name}` : ""}.
                {document.rejected_at
                  ? ` (${new Date(document.rejected_at).toLocaleDateString("pl-PL")})`
                  : ""}
              </p>
              {document.rejection_reason && (
                <p className="mt-1.5 text-xs text-red-700 dark:text-red-300/90 whitespace-pre-wrap">
                  <span className="font-semibold">Powód:</span> {document.rejection_reason}
                </p>
              )}
            </div>
          )}
          {isWithdrawn && (
            <div className="mt-3 rounded-md border border-gray-300 bg-gray-100/70 dark:border-gray-700 dark:bg-gray-800/60 px-3 py-2.5">
              <p className="text-xs text-gray-700 dark:text-gray-300">
                Dokument wycofany z akceptacji
                {document.withdrawn_by_name ? ` — ${document.withdrawn_by_name}` : ""}.
                {document.withdrawn_at
                  ? ` (${new Date(document.withdrawn_at).toLocaleDateString("pl-PL")})`
                  : ""}
              </p>
              {document.withdrawal_reason && (
                <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  <span className="font-semibold">Powód:</span> {document.withdrawal_reason}
                </p>
              )}
              {(() => {
                const file = document.revisions[0]?.original_file ?? null;
                return file?.url ? (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => downloadFileTracked(file.id, file.url!, file.original_filename)}
                      className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 hover:underline"
                    >
                      <Download size={12} />
                      {file.original_filename}
                    </button>
                    <FileDownloadStatusBadge fileId={file.id} />
                  </div>
                ) : null;
              })()}
            </div>
          )}
          {!isPendingApproval && !isRejected && !isWithdrawn && document.revisions.length === 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 pt-3">Brak rewizji.</p>
          )}
          {!isPendingApproval && !isRejected && !isWithdrawn && document.revisions.length > 0 && (
            <RevisionHistory
              document={document}
              cluster={cluster}
              isManager={isManager}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function ClusterDetailPage() {
  const { id: projectId, clusterId } = useParams<{ id: string; clusterId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  const isCoordinator = user?.role === "coordinator";
  const isCEO = user?.is_ceo ?? false;
  const isManager = isDirector || isCoordinator || isCEO;
  const isInternal = user?.role !== "contractor";
  const canSubmitPaperDocs = user?.has_submit_paper_docs_permission ?? false;

  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showEditCluster, setShowEditCluster] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showUnsettleConfirm, setShowUnsettleConfirm] = useState(false);
  const [unsettleError, setUnsettleError] = useState<string | null>(null);
  const deleteMutation = useDeleteCluster();
  const unsettleMutation = useUnsettleClusters();

  const resolvedClusterId = clusterId ?? "";

  const { data: cluster, isLoading: clusterLoading } = useDocumentationCluster(resolvedClusterId);
  usePageTitle(cluster?.name);
  const { data: rawDocuments = [], isLoading: docsLoading, refetch } = useClusterDocuments(resolvedClusterId);

  if (clusterLoading || docsLoading) return <PageLoader />;
  if (!cluster) return <div className="p-6 text-gray-500">Klaster nie istnieje.</div>;

  const userId = user?.id ? String(user.id) : null;
  const isDirectorAcceptance = cluster.cluster_type === "director_acceptance";

  // For director_acceptance clusters: only the reviewing_director can approve documents.
  // For designer_signature clusters: any cluster manager (CEO/director/coordinator) can approve.
  const canApproveDoc = isDirectorAcceptance
    ? userId !== null && userId === cluster.reviewing_director
    : isManager;

  // For director_acceptance: ALL documents always await director approval, regardless of role.
  // For designer_signature: managers auto-approve, others need approval.
  const needsApproval = isDirectorAcceptance ? true : !isManager;

  const documents = sortDocuments(rawDocuments, userId, isManager);

  const backHref = projectId ? `/projects/${projectId}/documentation` : "/documentation";
  const pendingApprovalDocs = documents.filter((d) => d.approval_status === "pending_approval");
  const pendingDocs = documents.filter((d) => d.approval_status === "approved" && d.current_status === "pending_signature");
  const signedDocs = documents.filter((d) => d.approval_status === "approved" && d.current_status === "signed");

  async function handleDelete() {
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(resolvedClusterId);
      navigate(backHref);
    } catch {
      setDeleteError("Nie udało się usunąć klastra.");
    }
  }

  async function handleUnsettle() {
    setUnsettleError(null);
    try {
      await unsettleMutation.mutateAsync({ cluster_ids: [resolvedClusterId] });
      setShowUnsettleConfirm(false);
    } catch (err) {
      const apiErr = (err as { response?: { data?: { errors?: unknown } } })
        ?.response?.data?.errors;
      setUnsettleError(
        typeof apiErr === "string"
          ? apiErr
          : "Nie udało się cofnąć rozliczenia.",
      );
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-4 sm:px-6 sm:py-5 mb-4 sm:mb-5">
        <Link
          to={backHref}
          className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 mb-3"
        >
          <ArrowLeft size={12} /> Dokumentacja projektowa
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 break-words">
              {cluster.name}
            </h1>
            <div className="mt-1">
              <Badge color={isDirectorAcceptance ? "purple" : "blue"}>
                {CLUSTER_TYPE_LABELS[cluster.cluster_type]}
              </Badge>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {isDirectorAcceptance ? (
                <>
                  Dyrektor sprawdzający:{" "}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {cluster.reviewing_director_name ?? "—"}
                  </span>
                </>
              ) : cluster.employee_signer_name ? (
                <>
                  Pracownik podpisujący:{" "}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {cluster.employee_signer_name}
                  </span>
                  {cluster.signature_type_detail && (
                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                      · {cluster.signature_type_detail.name}
                      {user?.is_ceo
                        ? cluster.signature_type_detail.is_free
                          ? " (darmowy)"
                          : cluster.signature_type_detail.rate
                            ? ` — ${cluster.signature_type_detail.rate} PLN / ${cluster.signature_type_detail.unit}`
                            : ""
                        : ""}
                    </span>
                  )}
                </>
              ) : (
                <>
                  Projektant:{" "}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {cluster.contractor_name ?? "—"}
                  </span>
                </>
              )}
            </p>
            {cluster.description && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {cluster.description}
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:shrink-0 w-full sm:w-auto">
            {isInternal && (cluster.signing_mode !== "paper" || canSubmitPaperDocs) && (
              <Button onClick={() => setShowAddDoc(true)} className="w-full sm:w-auto">
                <Plus size={14} className="mr-1" /> Dodaj dokument
              </Button>
            )}
            {cluster.can_edit && (
              <Button
                variant="ghost"
                onClick={() => setShowEditCluster(true)}
                className="w-full sm:w-auto"
              >
                <Pencil size={14} className="mr-1" /> Edytuj
              </Button>
            )}
            {cluster.can_manage_members && (
              <Button
                variant="ghost"
                onClick={() => setShowMembers(true)}
                className="w-full sm:w-auto"
              >
                <Users size={14} className="mr-1" /> Członkowie
                {cluster.members.length > 0 && ` (${cluster.members.length})`}
              </Button>
            )}
            {cluster.can_delete && (
              <Button
                variant="ghost"
                onClick={() => { setShowDeleteConfirm(true); setDeleteError(null); }}
                className="w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 border border-red-200 dark:border-red-800/40"
              >
                <Trash2 size={14} className="mr-1" /> Usuń klaster
              </Button>
            )}
          </div>
        </div>

        {cluster.signing_mode === "paper" && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 px-3 py-2 sm:px-4 text-sm text-amber-800 dark:text-amber-300 mt-3">
            <Printer className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              <strong>Klaster papierowy</strong> — dokumenty sprawdzone fizycznie w obiegu dokumentów.
              Nowe rewizje są dostępne i również trafiają od razu jako sprawdzone.
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 sm:flex sm:gap-6 gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="text-sm min-w-0">
            <span className="text-gray-400 dark:text-gray-500 uppercase text-[10px] sm:text-xs tracking-wide block truncate">Łącznie</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{documents.length}</span>
          </div>
          {isInternal && (
            <div className="text-sm min-w-0">
              <span className="text-gray-400 dark:text-gray-500 uppercase text-[10px] sm:text-xs tracking-wide block truncate">Ocz. na akceptację</span>
              <span className="font-semibold text-orange-600">{pendingApprovalDocs.length}</span>
            </div>
          )}
          {!isDirectorAcceptance && (
            <div className="text-sm min-w-0">
              <span className="text-gray-400 dark:text-gray-500 uppercase text-[10px] sm:text-xs tracking-wide block truncate">Ocz. na sprawdzenie</span>
              <span className="font-semibold text-amber-600">{pendingDocs.length}</span>
            </div>
          )}
          <div className="text-sm min-w-0">
            <span className="text-gray-400 dark:text-gray-500 uppercase text-[10px] sm:text-xs tracking-wide block truncate">
              {isDirectorAcceptance ? "Zaakceptowanych" : "Sprawdzonych"}
            </span>
            <span className="font-semibold text-green-600">{signedDocs.length}</span>
          </div>
        </div>
        {cluster.is_settled && (
          <div className="mt-4 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge color="gray">Rozliczony</Badge>
                  {cluster.settled_at && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(cluster.settled_at).toLocaleDateString("pl-PL")}
                    </span>
                  )}
                </div>
                <dl className="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                  {cluster.settled_by_name && (
                    <div>
                      Rozliczył:{" "}
                      <span className="text-gray-800 dark:text-gray-200">
                        {cluster.settled_by_name}
                      </span>
                    </div>
                  )}
                  {cluster.settlement_note && (
                    <div>
                      Notatka:{" "}
                      <span className="text-gray-800 dark:text-gray-200">
                        {cluster.settlement_note}
                      </span>
                    </div>
                  )}
                </dl>
              </div>
              {cluster.can_unsettle && !showUnsettleConfirm && (
                <Button
                  variant="ghost"
                  onClick={() => { setShowUnsettleConfirm(true); setUnsettleError(null); }}
                  className="text-sm shrink-0"
                >
                  Cofnij rozliczenie
                </Button>
              )}
            </div>
            {showUnsettleConfirm && (
              <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  Czy na pewno chcesz oznaczyć klaster{" "}
                  <strong>{cluster.name}</strong> jako nierozliczony?
                </p>
                {unsettleError && (
                  <p className="text-xs text-red-600 dark:text-red-400 mb-2">{unsettleError}</p>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={handleUnsettle}
                    disabled={unsettleMutation.isPending}
                    className="text-xs py-1 px-3"
                  >
                    {unsettleMutation.isPending ? "Cofanie…" : "Tak, cofnij rozliczenie"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowUnsettleConfirm(false)}
                    disabled={unsettleMutation.isPending}
                    className="text-xs py-1 px-3"
                  >
                    Anuluj
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        {showDeleteConfirm && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 dark:border-red-800/40 dark:bg-red-950/20 px-4 py-3">
            <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
              Czy na pewno chcesz usunąć klaster <strong>{cluster.name}</strong>?
            </p>
            <p className="text-xs text-red-700 dark:text-red-300 mb-3">
              Zostaną usunięte wszystkie dokumenty i rewizje. Tej operacji nie można cofnąć.
            </p>
            {deleteError && (
              <p className="text-xs text-red-600 dark:text-red-400 mb-2">{deleteError}</p>
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="text-xs py-1 px-3 text-white bg-red-600 hover:bg-red-700"
              >
                {deleteMutation.isPending ? "Usuwanie…" : "Tak, usuń klaster"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteMutation.isPending}
                className="text-xs py-1 px-3"
              >
                Anuluj
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Documents */}
      {documents.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center py-12 text-gray-400 dark:text-gray-500">
          <p>Brak dokumentów w tym klastrze.</p>
          {isInternal && (cluster.signing_mode !== "paper" || canSubmitPaperDocs) && (
            <Button onClick={() => setShowAddDoc(true)} variant="ghost" className="mt-3">
              Dodaj pierwszy dokument
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <DocumentRow
              key={doc.id}
              document={doc}
              cluster={cluster}
              isManager={isManager}
              canApproveDoc={canApproveDoc}
              onRefetch={refetch}
            />
          ))}
        </div>
      )}

      {showAddDoc && (
        <AddDocumentDialog
          clusterId={resolvedClusterId}
          signingMode={cluster.signing_mode}
          needsApproval={needsApproval}
          clusterType={cluster.cluster_type}
          onClose={() => setShowAddDoc(false)}
          onAdded={() => refetch()}
        />
      )}

      {showMembers && (
        <ClusterMembersDialog
          cluster={cluster}
          onClose={() => setShowMembers(false)}
        />
      )}

      {showEditCluster && (
        <EditClusterDialog
          cluster={cluster}
          onClose={() => setShowEditCluster(false)}
          onSaved={() => refetch()}
        />
      )}
    </div>
  );
}
